import React from 'react';
import { Button } from '../ui/button';
import { 
  Package, 
  X, 
  DollarSign, 
  Users, 
  Calendar,
  CheckCircle,
  Clock,
  GitBranch,
  AlertCircle,
  Eye,
  Edit,
  History,
  Zap,
  Settings,
  Smartphone,
  MessageSquare,
  HardDrive,
  CreditCard,
  Star,
  Globe,
  Shield
} from 'lucide-react';
import { Badge } from '../ui/Table';

export const PlanViewer = ({ plan, onClose, onEdit, onViewHistory }) => {
  if (!plan) {
    return null;
  }

  const currentVersion = plan.current_version;
  const pricing = currentVersion?.pricing || {};
  const allocations = currentVersion?.included_allocations || {};
  const features = currentVersion?.features || [];
  const tariffs = currentVersion?.tariffs || {};

  const formatAllocation = (value) => {
    if (value === -1) return 'Unlimited';
    if (value === null || value === undefined) return 'Not set';
    return value.toString();
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'Custom pricing';
    return `$${amount}`;
  };

  const getStatusColor = (isPublic) => {
    return isPublic 
      ? 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20'
      : 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20';
  };

  const allocationConfig = [
    { key: 'contacts_limit', label: 'Contacts Limit', icon: Users, unit: 'contacts' },
    { key: 'voice_minutes', label: 'Voice Minutes', icon: Smartphone, unit: 'minutes' },
    { key: 'sms_count', label: 'SMS Messages', icon: MessageSquare, unit: 'messages' },
    { key: 'storage_gb', label: 'Storage', icon: HardDrive, unit: 'GB' },
    { key: 'team_members', label: 'Team Members', icon: Users, unit: 'members' }
  ];

  const featureConfig = [
    { key: 'ai_insights', label: 'AI Insights', description: 'AI-powered relationship insights' },
    { key: 'gift_planning', label: 'Gift Planning', description: 'Gift reminder and planning tools' },
    { key: 'social_radar', label: 'Social Radar', description: 'Social media integration' },
    { key: 'advanced_analytics', label: 'Advanced Analytics', description: 'Detailed analytics and reporting' },
    { key: 'priority_support', label: 'Priority Support', description: '24/7 priority customer support' },
    { key: 'white_label', label: 'White Label', description: 'Custom branding solutions' },
    { key: 'api_access', label: 'API Access', description: 'Full API access for integrations' },
    { key: 'custom_fields', label: 'Custom Fields', description: 'Custom contact fields' },
    { key: 'team_collaboration', label: 'Team Collaboration', description: 'Multi-user collaboration' },
    { key: 'data_export', label: 'Data Export', description: 'Export data in various formats' }
  ];

  const annualSavings = pricing.monthly && pricing.annual 
    ? (pricing.monthly * 12 - pricing.annual)
    : 0;
  
  const savingsPercentage = pricing.monthly && pricing.annual && annualSavings > 0
    ? ((annualSavings / (pricing.monthly * 12)) * 100).toFixed(1)
    : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-5xl w-full max-h-screen overflow-y-auto dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Package className="w-6 h-6 text-indigo-600" />
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{plan.name}</h2>
                {plan.is_most_popular && (
                  <Badge variant="purple">
                    <Star className="w-3 h-3 mr-1" />
                    Most Popular
                  </Badge>
                )}
              </div>
              <p className="text-gray-600 dark:text-gray-300">{plan.description}</p>
              <div className="flex items-center gap-4 mt-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">Plan Key: {plan.key}</span>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(plan.is_public)}`}>
                  {plan.is_public ? <Globe className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {plan.is_public ? 'Public' : 'Draft'}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Version {currentVersion?.version || 1}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {onViewHistory && (
              <Button onClick={() => onViewHistory(plan.id, plan.name)} variant="outline" size="sm">
                <History className="w-4 h-4 mr-2" />
                View History
              </Button>
            )}
            {onEdit && (
              <Button onClick={() => onEdit(plan.id)} variant="outline" size="sm">
                <Edit className="w-4 h-4 mr-2" />
                Edit Plan
              </Button>
            )}
            <Button onClick={onClose} variant="outline" size="sm">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Pricing Information */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Pricing
              </h3>
              
              <div className="bg-gray-50 rounded-lg p-6 dark:bg-gray-800">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                      {formatCurrency(pricing.monthly)}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">per month</div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                      {formatCurrency(pricing.annual)}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">per year</div>
                  </div>
                </div>
                
                {annualSavings > 0 && (
                  <div className="mt-4 p-3 bg-green-50 rounded-lg dark:bg-green-900/10">
                    <div className="text-center text-green-700 dark:text-green-300">
                      <div className="font-medium">Annual Savings</div>
                      <div className="text-lg font-bold">${annualSavings.toFixed(2)} ({savingsPercentage}%)</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Usage Tariffs */}
            {Object.keys(tariffs).length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                  Usage Rates
                </h3>
                
                <div className="space-y-3">
                  {Object.entries(tariffs).map(([key, tariff]) => (
                    <div key={key} className="bg-gray-50 rounded-lg p-4 dark:bg-gray-800">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-gray-100 capitalize">
                            {key.replace(/_/g, ' ')}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-gray-900 dark:text-gray-100">
                            ${tariff.rate}/{key.includes('minutes') ? 'minute' : 'message'}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {tariff.currency}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Allocations and Features */}
          <div className="space-y-6">
            {/* Included Allocations */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-600" />
                Included Allocations
              </h3>
              
              <div className="space-y-3">
                {allocationConfig.map(({ key, label, icon: Icon, unit }) => {
                  const value = allocations[key];
                  return (
                    <div key={key} className="bg-gray-50 rounded-lg p-4 dark:bg-gray-800">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Icon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                          <span className="font-medium text-gray-900 dark:text-gray-100">{label}</span>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-gray-900 dark:text-gray-100">
                            {formatAllocation(value)}
                          </div>
                          {value !== -1 && value !== null && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">{unit}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Features */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-600" />
                Features ({features.length})
              </h3>
              
              {features.length > 0 ? (
                <div className="space-y-3">
                  {features.map(featureKey => {
                    const featureInfo = featureConfig.find(f => f.key === featureKey) || {
                      label: featureKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                      description: 'Custom feature'
                    };
                    
                    return (
                      <div key={featureKey} className="bg-gray-50 rounded-lg p-4 dark:bg-gray-800">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <div>
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {featureInfo.label}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-300">
                              {featureInfo.description}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg dark:bg-gray-800">
                  <Zap className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 dark:text-gray-300">No special features included</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">This plan includes basic functionality only</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Plan Metadata */}
        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-gray-600" />
            Plan Details
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-4 dark:bg-gray-800">
              <div className="text-sm text-gray-600 dark:text-gray-400">Display Order</div>
              <div className="font-bold text-gray-900 dark:text-gray-100">{plan.display_order}</div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 dark:bg-gray-800">
              <div className="text-sm text-gray-600 dark:text-gray-400">Current Version</div>
              <div className="font-bold text-gray-900 dark:text-gray-100">v{currentVersion?.version || 1}</div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 dark:bg-gray-800">
              <div className="text-sm text-gray-600 dark:text-gray-400">Created</div>
              <div className="font-bold text-gray-900 dark:text-gray-100">
                {new Date(plan.created_at).toLocaleDateString()}
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 dark:bg-gray-800">
              <div className="text-sm text-gray-600 dark:text-gray-400">Created By</div>
              <div className="font-bold text-gray-900 dark:text-gray-100">{plan.created_by || 'System'}</div>
            </div>
          </div>
        </div>

        {/* Version Information */}
        {currentVersion && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-indigo-600" />
              Version Information
            </h3>
            
            <div className="bg-gray-50 rounded-lg p-4 dark:bg-gray-800">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Version Number</div>
                  <div className="font-bold text-gray-900 dark:text-gray-100">v{currentVersion.version}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Status</div>
                  <div className="flex items-center gap-2">
                    <Badge variant={currentVersion.is_active ? 'success' : 'default'}>
                      {currentVersion.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Effective Date</div>
                  <div className="font-bold text-gray-900 dark:text-gray-100">
                    {new Date(currentVersion.effective_date || currentVersion.created_at).toLocaleDateString()}
                  </div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Created By</div>
                  <div className="font-bold text-gray-900 dark:text-gray-100">
                    {currentVersion.created_by || 'System'}
                  </div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Created</div>
                  <div className="font-bold text-gray-900 dark:text-gray-100">
                    {new Date(currentVersion.created_at).toLocaleDateString()}
                  </div>
                </div>
                
                {currentVersion.retired_date && (
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Retired</div>
                    <div className="font-bold text-red-600">
                      {new Date(currentVersion.retired_date).toLocaleDateString()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Stripe Integration Status */}
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-blue-600" />
            Stripe Integration
          </h3>
          
          <div className="bg-gray-50 rounded-lg p-4 dark:bg-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-900 dark:text-gray-100">Sync Status</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  Product synchronization with Stripe
                </div>
              </div>
              <div className="text-right">
                <Badge variant={plan.stripe_sync?.sync_status === 'synced' ? 'success' : 'warning'}>
                  {plan.stripe_sync?.sync_status || 'Unsynced'}
                </Badge>
                {plan.stripe_product_id && (
                  <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    ID: {plan.stripe_product_id}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanViewer;